import {z} from 'zod';
import type {FeatureConfig} from './internals/utils';

const IdentitySchema = z.object({
  userID: z.string().optional(),
  userAgent: z.string().optional(),
  ip: z.string().optional(),
  statsigEnvironment: z
    .object({
      tier: z.string().optional(),
    })
    .optional(),
  custom: z.object({
    app: z.string().optional(),
    serviceName: z.string().optional(),
    path: z.string().optional(),
    pathWithParams: z.string().optional(),
    siteCreated: z.string().optional(),
    siteShortName: z.string().optional(),
    userCreated: z.string().optional(),
    userVerified: z.string().optional(),
    workspaceCreated: z.string().optional(),
    workspaceSlug: z.string().optional(),
    isOveragesEnabled: z.string().optional(),
    bandwidthOveragesTranche: z.string().optional(),
    cloneOf: z.string().optional(),
    templateOf: z.string().optional(),
    isOnAIDADenylist: z.string().optional(),
    ruleCacheUpdated: z.string().optional(),
    hydrationDataCreated: z.string().optional(),
    procedureCall: z.string().optional(),
    webflowNextEnrolledFeatures: z.string().optional(),
    isEnterpriseWorkspace: z.string().optional(),
    isEnterpriseSite: z.string().optional(),
    webflowCloudAuthorization: z.string().optional(),
    sessionReplayConsent: z.string().optional(),
    isRealtimeEnabled: z.string().optional(),
    isInRealtimeRollout: z.string().optional(),
    partnerSandboxFor: z.string().optional(),
    awsRegion: z.string().optional(),
    siteKind: z.string().optional(),
    userPendoAudience: z.string().optional(),
    sitePnp42Holdout: z.string().optional(),
  }),
  customIDs: z.object({
    stableID: z.string().optional(),
    anonymousId: z.string().optional(),
    customId: z.string().optional(),
    githubId: z.string().optional(),
    siteId: z.string().optional(),
    workspaceId: z.string().optional(),
  }),
  privateAttributes: z.object({
    email: z.string().optional(),
  }),
});

const FeatureConfigOverridesSchema = z.object({
  configs: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
  flags: z.record(z.string(), z.boolean().optional()).optional(),
  experiments: z
    .record(z.string(), z.record(z.string(), z.unknown()))
    .optional(),
  layers: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});

export const FeatureConfigSchema = z.object({
  config: z.object({
    clientSdkKey: z.string(),
    tier: z.string(),
  }),
  identity: IdentitySchema.optional(),
  localMode: z.boolean().optional(),
  values: z.record(z.string(), z.unknown()).nullable().optional(),
  overrides: FeatureConfigOverridesSchema.optional(),
  initStrategy: z.enum(['sync', 'async']).optional(),
  hydrationDataCreatedTimestamp: z.number().optional(),
});

// Compile-time check: ensure the Zod schema stays in sync with the TS type.
// If FeatureConfig changes, this assignment will produce a type error.
const _assertSchemaMatchesType: (
  fc: FeatureConfig
) => z.infer<typeof FeatureConfigSchema> = (fc) => fc;
export {_assertSchemaMatchesType as _featureConfigTypeCheck};
